import { useEffect, useState } from 'react'
import { Card, Col, Container, Row } from 'react-bootstrap'
import { Bar, Doughnut } from 'react-chartjs-2'
import { useTranslateContext } from '../../contexts/translate.context'
import { usePartnerContext } from '../../contexts/partner.context'

import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import OperationHeader from '../headers/operation.header'
import styles from './ServiceCenterDashboard.module.scss'
import Lancha from '../../assets/images/Lancha.svg'
import Jet from '../../assets/images/Jetski.svg'
import { faker } from '@faker-js/faker'
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { ptBR } from 'date-fns/locale'
import Loader from '../../components/loader/loader'
import authService from '../../services/auth.service'
import lclService from '../../services/lcl.service'
import { floatMask, removeFormatDate } from '../../utilities/masks'
import { dateToString, toInt, toLowerCase, toStringFloat } from '../../utilities/auxiliary-functions'
import { VESSEL_JETSKI } from '../../utilities/constants'
import { isEmpty } from '../../utilities/validators'
import { Box } from '@mui/material'
import DashboardHeader from '../headers/dashboard.header'
import { StripBox, StripCard } from './components/StripBox'

interface IServiceCenterDashboardProps {
    
}

export const ServiceCenterDashboard: React.FC<IServiceCenterDashboardProps> = () => {
  
	let { partner } = usePartnerContext();
	const { display } = useTranslateContext();

    const [ loading, setLoading ] = useState(false)

    const [ dateFilter, setDateFilter ] = useState(new Date());

    const [ budgetedQty, setBudgetedQty ] = useState('');
    const [ approvedQty, setApprovedQty ] = useState('');
    const [ guaranteeQty, setGuaranteeQty ] = useState('');
    const [ budgetedTotal, setBudgetedTotal ] = useState('');
    const [ approvedTotal, setApprovedTotal ] = useState('');
    const [ guaranteeTotal, setGuaranteeTotal ] = useState('');
    const [ vessels, setVessels ] = useState([]);
    const [ budgets, setBudgets ] = useState([]);
    const [ payments, setPayments ] = useState([]);
    const [ grossRevenues, setGrossRevenues ] = useState([]);

    ChartJS.register(
        CategoryScale,
        LinearScale,
        BarElement,
        ChartDataLabels,
        Title,
        Tooltip,
        Legend,
        ArcElement
    )

    const dataType = {
        labels: vessels.map((vessel: any) => toInt(vessel.typeVessel) === VESSEL_JETSKI ? 'Jet-Ski' : 'Lancha'),
        datasets: [
        {
            data: vessels.map((vessel: any) => {
            var some = 0;
            vessels.forEach((v: any) => {
                some += v.qty
            });

            return some > 0 ? Math.round((vessel.qty / some) * 100) : 0
            }),
            backgroundColor: ['#00007A', '#91A9AF'],
            borderWidth: 0
        }
        ]
    }

    const dataTypeBudget = {
        labels: budgets.filter((budget: any) => budget.description !== 'MAINTENANCE')
        .map((budget: any) => {
            switch (budget.description) {
            case 'ACTIVED':
                return 'Orçado'            
            case 'CANCELED':
                return 'Cancelado'
            default:
                return 'Aprovado'
            } 
        }), // ['Orçado', 'Aprovado', 'Cancelado', 'Garantia'],
        datasets: [
        {
            data: budgets.map((budget: any) => {
            var some = 0;
            budgets.forEach((v: any) => {
                some += v.qty
            });

            return some > 0 ? Math.round((budget.qty / some) * 100) : 0
            }),
            backgroundColor: ['#4264E0', '#6CC94C', '#911116', '#6783E6'],
            borderWidth: 0
        }
        ]
    }

    const dataPayment = {
        labels: payments.map((payment: any) => display.label[toLowerCase(payment.form)]), // ['Pix', 'Dinheiro', 'Cartão', 'Boleto'],
        datasets: [
        {
            data: payments.map((payment: any) => payment.total),
            backgroundColor: ['#16BE94', '#E5C710', '#6783E6', '#FAA55C'],
            borderWidth: 0
        }
        ]
    }

    const typeOptions = {
        responsive: true,
        cutout: 60,
        gridLines: {
            display: false
        },
        plugins: {
            legend: {
                position: 'bottom' as const,
                labels: {
                color: '#FFFFFF',
                font: {
                    size: 12,
                    family: "'Montserrat', sans-serif"
                }
                }
            },
            datalabels: {
                color: 'white',
                font: {
                    size: 12,
                    family: "'Montserrat', sans-serif"
                },
                formatter(value: any) {
                    return `${value}%`
                }
            }
        }
    }

    const paymentOptions = {
        ...typeOptions,
        responsive: true,
        cutout: 60,
        gridLines: {
            display: false
        },
        plugins: {
            ...typeOptions.plugins,
            datalabels: {
                color: 'white',
                font: {
                    size: 12,
                    family: "'Montserrat', sans-serif"
                },
                formatter(value: any) {
                 return `R$ ${floatMask(value)}`
                }
            }
        }
    }

    const budgetOptions = {
        responsive: true,
        gridLines: {
            display: false
        },
        plugins: {
            legend: {
                position: 'bottom' as const,
                labels: {
                display: false,
                color: '#FFFFFF',
                font: {
                    size: 12,
                    family: "'Montserrat', sans-serif"
                }
                }
            },
            datalabels: {
                color: 'white',
                font: {
                size: 12,
                family: "'Montserrat', sans-serif"
                },
                formatter(value: any) {
                return `R$ ${floatMask(value)}`
                }
            }
        },
        maintainAspectRatio: true,
        scales: {
            x: {
                ticks: {
                    display: false,
                    color: 'white',
                    font: {
                        size: 12,
                        family: "'Montserrat', sans-serif"
                    }
                },
                grid: {
                    display: true,
                    tickColor: 'white',
                    drawOnChartArea: false,
                    color: 'white'
                },
                border: {
                    color: 'white'
                }
            },
            y: {
                ticks: {
                    color: 'white',
                    font: {
                        size: 12,
                        family: "'Montserrat', sans-serif"
                    }
                },
                grid: {
                    display: true,
                    tickColor: 'white',
                    drawOnChartArea: false,
                    color: 'white'
                },
                border: {
                    color: 'white'
                }
            }
        }
    }

    const profitOptions = {
        ...budgetOptions,
        plugins: {
        ...budgetOptions.plugins,
        datalabels: {
            color: 'white',
            font: {
            size: 12,
            family: "'Montserrat', sans-serif"
            },
            formatter(value: any) {
            return `R$ ${floatMask(value)}`
            }
        }
        }
    }

    const budgetsLabels = [
        'Legenda'
    ]

    const budgetData = {
        labels: budgetsLabels,
        datasets: [
        {
            label: 'Orçado',
            data: budgets.filter((budget: any) => budget.description === 'ACTIVED' ).map((budget: any) => budget.total || 0),
            backgroundColor: '#4264E0',
        },
        {
            label: 'Aprovado',
            data: budgets.filter((budget: any) => budget.description === 'DELIVERED' ).map((budget: any) => budget.total || 0),
            backgroundColor: '#6CC94C'
        },
        {
            label: 'Cancelado',
            data: budgets.filter((budget: any) => budget.description === 'CANCELED' ).map((budget: any) => budget.total || 0),
            backgroundColor: '#911116'
        }
        ]
    }

    const profitLabels = [
        'Legenda'
    ]

    const profitData = {
        labels: profitLabels,
        datasets: [
        {
            label: 'Peças',
            data: grossRevenues.map((grossRevenue: any) => grossRevenue.productTotal),
            backgroundColor: '#5B6E95',
            barPercentage: 0.4,
        },
        {
            label: 'Serviços',
            data: grossRevenues.map((grossRevenue: any) => grossRevenue.officeTotal),
            backgroundColor: '#819F8C',
            barPercentage: 0.4,
        },
        ]
    }

    async function searchFilter(dateSearch: Date | null) {
        if (dateSearch === null)
            dateSearch = new Date();

        setDateFilter(dateSearch)
        const view = await lclService.serviceCenter(partner.id, removeFormatDate(dateToString(dateSearch, 'dd/mm/yyyy')), 7);

        setBudgetedQty(view.budgetedQty);
        setApprovedQty(view.approvedQty);
        setGuaranteeQty(view.guaranteeQty);
        setBudgetedTotal(view.budgetedTotal);
        setApprovedTotal(view.approvedTotal);
        setGuaranteeTotal(view.guaranteeTotal);
        setVessels(view.vessels);
        setBudgets(view.budgets);
        setPayments(view.payments);
        setGrossRevenues(view.grossRevenues);

        setLoading(false)
    }

    useEffect(() => {
        if (isEmpty(partner))
            // eslint-disable-next-line react-hooks/exhaustive-deps
            partner = authService.currentPartner();

        setLoading(true)
        setTimeout(() => {
            searchFilter(new Date())
            setLoading(false)
        }, 500)
    }, [])
    
    function viewDashboard() {
        return (
            <Container fluid className="page-body dashboards">
                <Box className="dashboards-search">
                    <LocalizationProvider
                        adapterLocale={ptBR}
                        dateAdapter={AdapterDateFns}
                    >
                        <DesktopDatePicker
                            label="Buscar período"
                            sx={{ width: 160, fontSize: 42 }}
                            slotProps={{
                                textField: {
                                variant: 'standard',
                                helperText: 'Exibindo 7 dias',
                                }
                            }}
                            value={dateFilter} 
                            onChange={(date) => searchFilter(date)}
                            className="mt-2 me-3"
                            // minDate={new Date()}
                        />
                    </LocalizationProvider>
                </Box>
                <StripBox>
                    <StripCard className="orange" label="Total Orçado" text={budgetedQty} value={`R$ ${toStringFloat(budgetedTotal)}`} />
                    <StripCard className="green" label="Total Aprovado" text={approvedQty} value={`R$ ${toStringFloat(approvedTotal)}`} />
                    <StripCard className="blue" label="Garantia" text={guaranteeQty} value={`R$ ${toStringFloat(guaranteeTotal)}`} />
                </StripBox>
                <Box className="d-flex flex-column gap-2">
                    <Box className="d-flex justify-content-between gap-2">
                        <Card>
                            <Card.Header>
                                <Card.Title>Embarcações</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <Doughnut data={dataType} options={typeOptions}/>
                            </Card.Body>
                        </Card>
                        <Card className='flex-grow-1'>
                            <Card.Header>
                                <Card.Title>Orçamentos (R$)</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <Bar options={budgetOptions} data={budgetData} />
                            </Card.Body>
                        </Card>
                        <Card>
                            <Card.Header>
                                <Card.Title>Orçamentos (%)</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <Doughnut data={dataTypeBudget} options={typeOptions} />
                            </Card.Body>
                        </Card>
                    </Box>
                    <Box className="d-flex justify-content-between gap-2">
                        <Card>
                            <Card.Header>
                                <Card.Title>Formas de Pagamento</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <Doughnut data={dataPayment} options={paymentOptions} />
                            </Card.Body>
                        </Card>
                        <Card className='flex-grow-1'>
                            <Card.Header>
                                <Card.Title>Receita Bruta</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <Bar className="mt-5" options={profitOptions} data={profitData} />
                            </Card.Body>
                        </Card>
                    </Box>
                </Box>
            </Container>
        )
    }

    return (
        <div className="dashboard">
            <DashboardHeader title="Gestão Oficina" description="" />
            {loading ? <Loader /> : viewDashboard()}
        </div>
    )
}

export default ServiceCenterDashboard
